import type { PublicDestinationMarketStats } from "@otalytics/shared";

/**
 * City-page copy. Every figure is a field the report already renders.
 * A clause whose field is missing is omitted — never filled with a guess.
 *
 * The review window is the same 30 days `REPORT_WINDOW_DAYS` uses in
 * `destination-report.service.ts`. The free leaderboard length is the
 * API's `FREE_LEADERBOARD_ROWS` (3).
 */
const REVIEW_WINDOW_DAYS = 30;

export interface CityGlanceGroup {
  representativeTitle: string | null;
  tourCount: number;
  bestPosition: number;
}

export interface CityGlanceOperator {
  reviewSharePct: number | null;
}

export interface CityGlanceInput {
  city: string;
  platform: string;
  medianPriceEur: number | null;
  priceRangeEur: { min: number; max: number } | null;
  topTenMedianReviewCount: number | null;
  medianReviewCount: number | null;
  toursRanked: number;
  medianRating: number | null;
  groups: readonly CityGlanceGroup[];
  operatorCount: number;
  operatorRows: readonly CityGlanceOperator[];
}

export function destinationPageTitle(city: string, platform: string): string {
  return `${city} tour rankings & prices on ${platform} | OTAlytics`;
}

export function destinationPageDescription(city: string, platform: string): string {
  return `Who ranks on ${platform}'s first page in ${city}, what each tour charges, and which operators win new reviews. Free preview, re-checked weekly.`;
}

export function destinationH1(city: string, platform: string): string {
  return `${city} tour rankings on ${platform}: who ranks and what they charge`;
}

export function destinationIntro(city: string, platform: string): readonly string[] {
  return [
    `If you sell tours in ${city}, the first page of ${platform} is your shop window and your competition at once. This page shows the tours that hold it: who runs them, what they charge, how many reviews sit behind each position, and how the page is shifting from week to week.`,
    `It's built for operators who sell in one city, not for travellers. Read the free preview below to see where the market sits. Unlock the full ${city} report to see every ranked tour, every operator competing for the page, and what changed this month.`,
  ];
}

export function glanceHeading(city: string): string {
  return `${city} at a glance`;
}

export function reportContentsHeading(city: string): string {
  return `What's in the ${city} report`;
}

export function faqHeading(city: string): string {
  return `${city} report: questions operators ask`;
}

export function purchaseHeading(city: string): string {
  return `See the whole ${city} first page`;
}

export function purchaseLede(): string {
  return "Every ranked tour, every competing operator, and what moved this month. Published monthly and yours to keep.";
}

export function purchaseButtonLabel(city: string, price: string): string {
  return `Unlock the full ${city} report: ${price} a month`;
}

export const CITY_REPORT_PRICING_LINK = {
  href: "/pricing",
  label: "Compare city reports and tracking plans",
} as const;

export interface CityReportLink {
  href: string;
  label: string;
}

export function priceGuideLink(city: string): CityReportLink {
  return {
    href: "/blog/tour-pricing-strategy",
    label:
      city === "Rome"
        ? "How to price a Rome tour against the page"
        : `How to price a ${city} tour against the page`,
  };
}

/** GetYourGuide ranking guide. Omitted on other platforms — the post is about GetYourGuide. */
export function rankGuideLink(platformName: string): CityReportLink | null {
  if (platformName !== "GetYourGuide") {
    return null;
  }
  return {
    href: "/blog/getyourguide-supplier-guide",
    label: "How GetYourGuide decides who ranks",
  };
}

export interface ReportSectionNote {
  title: string;
  body: string;
  link?: CityReportLink;
}

/**
 * Prose for the blocks the bento already draws. A note is included only when
 * the tile that carries that figure will render.
 */
export function reportSectionNotes(
  city: string,
  market: PublicDestinationMarketStats,
): ReportSectionNote[] {
  const notes: ReportSectionNote[] = [];

  if (market.medianPriceEur != null) {
    const hasTrend =
      market.priceTrend != null && market.priceTrend.changePct !== 0;
    const movement = hasTrend
      ? ", plus how far the median has moved since the tracking window opened"
      : "";
    notes.push({
      title: "What the page charges",
      body: `The lowest, median and highest "from" price across every tour on the first page${movement}. Prices are the figures printed on each tour card, converted to euro. Use it to see whether your price sits inside the band where ${city}'s first-page tours actually sell.`,
      link: priceGuideLink(city),
    });
  }

  const hasTopReviews = market.topTenMedianReviewCount != null;
  const hasTopPrice = market.topTenMedianPriceEur != null;
  if (hasTopReviews || hasTopPrice) {
    notes.push({
      title: "The top ten, against the page",
      body: topTenNote(city, hasTopReviews, hasTopPrice),
    });
  }

  if (market.medianRating != null) {
    notes.push({
      title: "Typical standing",
      body:
        market.medianReviewCount != null
          ? "The median rating on the page and the review count that half the tours exceed: a quick benchmark for where your own listing stands."
          : "The median rating on the page: a quick benchmark for where your own listing stands.",
    });
  }

  return notes;
}

function topTenNote(city: string, hasReviews: boolean, hasPrice: boolean): string {
  if (hasReviews && hasPrice) {
    return `The median review count and median price of the top ten, compared with the whole first page. It shows how much review volume it takes to sit near the top in ${city}, and whether the leaders charge more or less than everyone else.`;
  }
  if (hasReviews) {
    return `The median review count of the top ten, compared with the whole first page. It shows how much review volume it takes to sit near the top in ${city}.`;
  }
  return `The median price of the top ten, compared with the whole first page. It shows whether the leaders in ${city} charge more or less than everyone else.`;
}

export function groupsDescription(city: string): string {
  const examples =
    city === "Rome"
      ? ", for example Vatican Museums and Sistine Chapel tours, Pompeii and Amalfi Coast day trips, or private golf-cart city tours (the groups change with the page)"
      : " (the groups change with the page)";
  return `Tours grouped by how alike they are${examples}. Each group shows how many tours it holds, its best rank, a typical price and its price range on one shared scale. Price against the tours you actually compete with, not against the whole city.`;
}

export function poiSectionBlurb(city: string): string {
  return `Separate first-page leaderboards for ${city}'s main sights, each with its own page.`;
}

export function leaderboardSectionBlurb(shown: number, total: number): string {
  const base =
    'Rank, tour, operator, rating, review count and "from" price for every ranked tour, with movement arrows where a tour changed position.';
  if (shown < total) {
    const shownLabel = shown === 3 ? "three" : String(shown);
    return `${base} The free preview shows the top ${shownLabel}. The full report shows all of them.`;
  }
  return base;
}

export function operatorSectionBlurb(city: string): string {
  return `The suppliers holding the most first-page positions in ${city}: how many tours each has on the page, its best rank, and its share of new reviews over the last ${REVIEW_WINDOW_DAYS} days. Recent review share is a useful public proxy for who is taking bookings right now, not just who ranked well a year ago.`;
}

const REVIEW_GAP_FAR =
  "the top of the page is held by tours with far more reviews than the rest";
const REVIEW_GAP_CLOSE =
  "review counts at the top are not far ahead of the rest of the page";

function formatEur(value: number): string {
  return `€${Math.round(value)}`;
}

function formatCount(value: number): string {
  return value.toLocaleString("en-GB");
}

function reviewGapSentence(topTen: number, pageMedian: number): string {
  return topTen >= 2 * pageMedian ? REVIEW_GAP_FAR : REVIEW_GAP_CLOSE;
}

function largestGroupLabels(groups: readonly CityGlanceGroup[]): string[] {
  return [...groups]
    .filter(
      (group): group is CityGlanceGroup & { representativeTitle: string } =>
        group.representativeTitle != null && group.representativeTitle.trim() !== "",
    )
    .sort(
      (a, b) => b.tourCount - a.tourCount || a.bestPosition - b.bestPosition,
    )
    .slice(0, 3)
    .map((group) => group.representativeTitle);
}

/** Same label the groups block uses, so a comma inside a title stays inside one group. */
function groupLabel(title: string): string {
  return `tours like \u201C${title}\u201D`;
}

function groupSentence(labels: readonly string[]): string | null {
  const named = labels.map(groupLabel);
  if (named.length === 0) {
    return null;
  }
  if (named.length === 1) {
    return `The largest group of similar tours on the page was ${named[0]}.`;
  }
  if (named.length === 2) {
    return `The largest groups of similar tours on the page were ${named[0]} and ${named[1]}.`;
  }
  return `The largest groups of similar tours on the page were ${named[0]}, ${named[1]} and ${named[2]}.`;
}

function formatShare(value: number): string {
  const tenths = Math.round(value * 10);
  const whole = tenths / 10;
  const text = tenths % 10 === 0 ? String(whole) : whole.toFixed(1);
  return `${text}%`;
}

function sumShares(values: readonly number[]): number {
  const tenths = values.reduce((sum, value) => sum + Math.round(value * 10), 0);
  return tenths / 10;
}

function operatorSentence(
  operatorCount: number,
  rows: readonly CityGlanceOperator[],
): string | null {
  const top = rows.slice(0, 3);
  const shares = top.map((row) => row.reviewSharePct);
  const shareSum =
    shares.length > 0 && shares.every((share): share is number => share != null)
      ? sumShares(shares)
      : null;

  const clauses: string[] = [];
  if (operatorCount > 0) {
    const noun = operatorCount === 1 ? "operator shares" : "operators share";
    clauses.push(
      `${formatCount(operatorCount)} ${noun} the first page`,
    );
  }
  if (shareSum != null) {
    const who =
      top.length <= 1
        ? "the biggest took"
        : top.length === 2
          ? "the two biggest took"
          : "the three biggest took";
    clauses.push(
      `${who} ${formatShare(shareSum)} of new reviews in the last ${REVIEW_WINDOW_DAYS} days`,
    );
  }
  if (clauses.length === 0) {
    return null;
  }
  if (clauses.length === 1) {
    return `${clauses[0]}.`;
  }
  return `${clauses[0]}, and ${clauses[1]}.`;
}

/**
 * Server-rendered summary. Returns the paragraphs that have at least one
 * real figure. An empty array means the page should not render the section.
 */
export function buildCityGlance(input: CityGlanceInput): string[] {
  const sentences: string[] = [];

  if (input.medianPriceEur != null && input.priceRangeEur) {
    sentences.push(
      `On the last check, the median tour on ${input.city}'s ${input.platform} first page cost ${formatEur(input.medianPriceEur)}, with prices running from ${formatEur(input.priceRangeEur.min)} to ${formatEur(input.priceRangeEur.max)}.`,
    );
  } else if (input.medianPriceEur != null) {
    sentences.push(
      `On the last check, the median tour on ${input.city}'s ${input.platform} first page cost ${formatEur(input.medianPriceEur)}.`,
    );
  } else if (input.priceRangeEur) {
    sentences.push(
      `On the last check, prices on ${input.city}'s ${input.platform} first page ran from ${formatEur(input.priceRangeEur.min)} to ${formatEur(input.priceRangeEur.max)}.`,
    );
  }

  if (input.topTenMedianReviewCount != null) {
    const parts = [
      `The top ten carried a median of ${formatCount(input.topTenMedianReviewCount)} reviews`,
    ];
    if (input.medianReviewCount != null && input.toursRanked > 0) {
      parts.push(
        `against ${formatCount(input.medianReviewCount)} across all ${formatCount(input.toursRanked)} ranked tours`,
      );
      parts.push(
        `so ${reviewGapSentence(input.topTenMedianReviewCount, input.medianReviewCount)}`,
      );
    } else if (input.medianReviewCount != null) {
      parts.push(
        `so ${reviewGapSentence(input.topTenMedianReviewCount, input.medianReviewCount)}`,
      );
    }
    sentences.push(`${parts.join(", ")}.`);
  }

  if (input.medianRating != null) {
    sentences.push(
      `The median rating on the page was ${input.medianRating.toFixed(1)}.`,
    );
  }

  const paragraphs: string[] = [];
  if (sentences.length > 0) {
    paragraphs.push(sentences.join(" "));
  }

  const second = [
    groupSentence(largestGroupLabels(input.groups)),
    operatorSentence(input.operatorCount, input.operatorRows),
  ].filter((sentence): sentence is string => sentence != null);
  if (second.length > 0) {
    paragraphs.push(second.join(" "));
  }

  return paragraphs;
}

export interface FaqSegment {
  text: string;
  href?: string;
}

export interface CityFaqItem {
  question: string;
  segments: readonly FaqSegment[];
}

export interface CityFaqInput {
  city: string;
  /** City-only questions. Omitted, or any slug other than one with extras, keeps the shared list. */
  citySlug?: string;
  platform: string;
  proxyCountryName: string;
  price: string;
}

export function faqPlainAnswer(item: CityFaqItem): string {
  return item.segments.map((segment) => segment.text).join("");
}

function cityFaqExtras(citySlug: string | undefined): CityFaqItem[] {
  if (citySlug === "porto") {
    return [
      {
        question: "Does the Porto report include Douro Valley day trips?",
        segments: [
          {
            text: "Yes, when they rank on GetYourGuide's first page for Porto, and on the last check the top of that page was Douro Valley day trips with wine, lunch and a boat. The ",
          },
          {
            text: "Porto field note",
            href: "/blog/selling-tours-in-porto-getyourguide",
          },
          {
            text: " walks through what that page charges and where a single-city operator can find room.",
          },
        ],
      },
      {
        question: "Do I need to register to sell tours in Porto?",
        segments: [
          {
            text: "Companies that sell tourist-activity services (animação turística) commercially in Portugal must register in Turismo de Portugal's RNAAT and hold the required insurance. Our ",
          },
          {
            text: "Porto field note",
            href: "/blog/selling-tours-in-porto-getyourguide",
          },
          {
            text: " points at those national rules, which apply in Porto too, and at the boat rules if you run a Douro trip yourself. This isn't legal advice.",
          },
        ],
      },
    ];
  }
  if (citySlug !== "lisbon") {
    return [];
  }
  return [
    {
      question: "Does the Lisbon report include Sintra day trips?",
      segments: [
        {
          text: "Yes, when they rank on GetYourGuide's first page for Lisbon, and on the last check the largest group of similar tours on the page was Sintra day trips. Sintra also has its own first page and report, with separate leaderboards for Pena Palace, Quinta da Regaleira, Cabo da Roca and the Sintra Mountains: ",
        },
        { text: "Sintra report", href: "/destinations/sintra" },
        { text: "." },
      ],
    },
    {
      question: "Do I need to register to sell tours in Lisbon?",
      segments: [
        {
          text: "Companies that sell tourist-activity services (animação turística) commercially in Portugal must register in Turismo de Portugal's RNAAT and hold the required insurance. Our ",
        },
        {
          text: "Lisbon field note",
          href: "/blog/selling-tours-in-lisbon-getyourguide",
        },
        {
          text: " covers the rules operators ask about, including guides and tuk-tuks. This isn't legal advice.",
        },
      ],
    },
  ];
}

export function cityReportFaq(input: CityFaqInput): CityFaqItem[] {
  const seenFrom =
    input.proxyCountryName.trim() !== ""
      ? ` as seen from ${input.proxyCountryName}`
      : "";

  const items: CityFaqItem[] = [
    {
      question: `Is the ${input.city} preview free?`,
      segments: [
        {
          text: "Yes. The price band, top-ten benchmarks, tour groups, points of interest, the top three tours and the leading suppliers are free to view with no account.",
        },
      ],
    },
    {
      question: `What do I get for ${input.price} a month?`,
      segments: [
        {
          text: `The full ${input.city} report: every ranked tour on the first page, every operator competing in ${input.city} with an estimated share of turnover, this month's position changes, short readings of the market, and full leaderboards for every ${input.city} point of interest. A new issue is published every month and stays in your library. Cancel any time.`,
        },
      ],
    },
    {
      question: `Which platform does the ${input.city} report cover?`,
      segments: [
        {
          text: `${input.platform}. The rankings are ${input.platform}'s first page for ${input.city}${seenFrom}.`,
        },
      ],
    },
    {
      question: "How often is the data updated?",
      segments: [
        {
          text: `Each ${input.city} page is re-checked at least weekly, and the full report is published as a monthly issue.`,
        },
      ],
    },
    {
      question: "Do sponsored listings count as rankings?",
      segments: [
        {
          text: "No. Sponsored and promotional tiles are skipped, so every position shown is an organic one.",
        },
      ],
    },
    {
      question: "Do I need to set up my own tours or keywords?",
      segments: [
        {
          text: "No. A city report needs no tours to configure and spends no credits. If you also want to track your own listings on specific keywords, that's the separate tracking plans on the ",
        },
        { text: "pricing page", href: "/pricing" },
        { text: "." },
      ],
    },
    {
      question: "Will my tour appear in the report?",
      segments: [
        {
          text: `If it ranks on ${input.platform}'s first page for ${input.city}, or for one of the ${input.city} points of interest we track, it's in the report with its rank, rating, review count and price.`,
        },
      ],
    },
    {
      question: "Why are prices in euro?",
      segments: [
        {
          text: "Prices are taken from each tour card and converted to euro, so every tour is compared on one scale.",
        },
      ],
    },
    {
      question: "How do I get an account?",
      segments: [
        {
          text: "Paying creates your account with the email you pay with. There's no separate sign-up step.",
        },
      ],
    },
  ];

  const extras = cityFaqExtras(input.citySlug);
  if (extras.length === 0) {
    return items;
  }
  const accountIndex = items.findIndex((item) =>
    item.question.startsWith("How do I get an account"),
  );
  if (accountIndex < 0) {
    return [...items, ...extras];
  }
  return [
    ...items.slice(0, accountIndex),
    ...extras,
    ...items.slice(accountIndex),
  ];
}

export function faqPageJsonLd(
  items: readonly CityFaqItem[],
): {
  "@context": "https://schema.org";
  "@type": "FAQPage";
  mainEntity: {
    "@type": "Question";
    name: string;
    acceptedAnswer: { "@type": "Answer"; text: string };
  }[];
} {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faqPlainAnswer(item),
      },
    })),
  };
}
